import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  Lock,
  Shield,
} from 'lucide-react';
import {
  asApiResponseError,
  CompanyAuthorityBasis,
  CompanyDataAuthorization,
  createProposalAuthorization,
  isProposalAuthorizationCompatible,
  listCompanyDataAuthorizations,
  PROPOSAL_AUTHORIZATION_NOTICE_VERSION,
  PROPOSAL_EXTERNAL_PROCESSORS,
  withdrawCompanyDataAuthorization,
} from '../services/companyDataAuthorizationService';

interface CompanyDataAuthorizationPanelProps {
  companyId: string | null | undefined;
  value: string | null;
  onChange: (authorizationId: string | null) => void;
  className?: string;
}

const formatDate = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString('ko-KR');
};

const CompanyDataAuthorizationPanel: React.FC<
  CompanyDataAuthorizationPanelProps
> = ({ companyId, value, onChange, className = '' }) => {
  const [authorizations, setAuthorizations] = useState<
    CompanyDataAuthorization[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authorityBasis, setAuthorityBasis] = useState<
    CompanyAuthorityBasis | ''
  >('');
  const [confirmedAuthority, setConfirmedAuthority] = useState(false);
  const [confirmedProcessing, setConfirmedProcessing] = useState(false);
  const [confirmedExternal, setConfirmedExternal] = useState(false);

  const compatible = useMemo(
    () =>
      authorizations
        .filter((item) => isProposalAuthorizationCompatible(item))
        .sort(
          (left, right) =>
            Date.parse(right.granted_at) - Date.parse(left.granted_at),
        ),
    [authorizations],
  );
  const selected = compatible.find((item) => item.id === value);

  useEffect(() => {
    onChange(null);
    setAuthorizations([]);
    setError(null);
    setNotice(null);
    setShowConsent(false);
    setShowWithdrawal(false);
    setWithdrawalReason('');
    setAuthorityBasis('');
    setConfirmedAuthority(false);
    setConfirmedProcessing(false);
    setConfirmedExternal(false);

    if (!companyId) return;

    const controller = new AbortController();
    setLoading(true);
    listCompanyDataAuthorizations(companyId, controller.signal)
      .then((result) => {
        const usable = result.items
          .filter((item) => isProposalAuthorizationCompatible(item))
          .sort(
            (left, right) =>
              Date.parse(right.granted_at) - Date.parse(left.granted_at),
        );
        setAuthorizations(result.items);

        const selected = usable[0] || null;
        if (selected) {
          onChange(selected.id);
        } else {
          setShowConsent(true);
        }
      })
      .catch((caught: unknown) => {
        if (
          caught instanceof DOMException &&
          caught.name === 'AbortError'
        ) {
          return;
        }
        const message =
          typeof caught === 'object' &&
          caught !== null &&
          'message' in caught &&
          typeof caught.message === 'string'
            ? caught.message
            : '기업 자료 처리 동의를 확인하지 못했습니다.';
        setError(message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [companyId, onChange]);

  const selectAuthorization = (authorizationId: string) => {
    if (!companyId) return;
    onChange(authorizationId);
    setShowWithdrawal(false);
    setWithdrawalReason('');
    setError(null);
    setNotice(null);
  };

  const handleCreate = async () => {
    if (
      !companyId ||
      !authorityBasis ||
      !confirmedAuthority ||
      !confirmedProcessing ||
      !confirmedExternal
    ) {
      setError('모든 권한·처리·외부 제공 확인 항목에 동의해 주세요.');
      return;
    }

    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      const created = await createProposalAuthorization(
        companyId,
        authorityBasis,
      );
      setAuthorizations((current) => [created, ...current]);
      onChange(created.id);
      setShowConsent(false);
      setNotice('기업 자료 처리 동의를 등록하고 선택했습니다.');
    } catch (caught: unknown) {
      const apiError = asApiResponseError(caught);
      const message =
        apiError?.message ||
        '기업 자료 처리 동의를 등록하지 못했습니다.';
      if (apiError?.status === 409) {
        try {
          const refreshed = await listCompanyDataAuthorizations(companyId);
          setAuthorizations(refreshed.items);
          const latest = refreshed.items
            .filter((item) => isProposalAuthorizationCompatible(item))
            .sort(
              (left, right) =>
                Date.parse(right.granted_at) -
                Date.parse(left.granted_at),
            )[0];
          onChange(latest?.id || null);
          setShowConsent(!latest);
        } catch {
          // Keep the original conflict error if refreshing also fails.
        }
      }
      setError(
        message.includes('역할') || message.includes('권한')
          ? `${message} 기업 소유자 또는 관리자에게 동의 등록을 요청해 주세요.`
          : message,
      );
    } finally {
      setCreating(false);
    }
  };

  const handleWithdraw = async () => {
    if (!companyId || !selected) return;
    const reason = withdrawalReason.trim();
    if (reason.length < 3) {
      setError('철회 사유를 3자 이상 입력해 주세요.');
      return;
    }

    setWithdrawing(true);
    setError(null);
    setNotice(null);
    try {
      const withdrawn = await withdrawCompanyDataAuthorization(
        companyId,
        selected.id,
        reason,
      );
      setAuthorizations((current) =>
        current.map((item) =>
          item.id === withdrawn.id ? withdrawn : item,
        ),
      );
      onChange(null);
      setShowWithdrawal(false);
      setWithdrawalReason('');
      setShowConsent(true);
      setNotice(
        '동의를 철회했습니다. 이후 작업을 하려면 새 동의가 필요합니다.',
      );

      let refreshed;
      try {
        refreshed = await listCompanyDataAuthorizations(companyId);
      } catch {
        setError(
          '동의는 철회되었지만 최신 동의 목록을 다시 불러오지 못했습니다.',
        );
        return;
      }
      setAuthorizations(refreshed.items);
      const next = refreshed.items
        .filter((item) => isProposalAuthorizationCompatible(item))
        .sort(
          (left, right) =>
            Date.parse(right.granted_at) - Date.parse(left.granted_at),
        )[0];
      onChange(next?.id || null);
      setShowConsent(!next);
      setNotice(
        next
          ? '선택한 동의를 철회하고 다른 활성 동의를 선택했습니다.'
          : '동의를 철회했습니다. 이후 작업을 하려면 새 동의가 필요합니다.',
      );
    } catch (caught: unknown) {
      const apiError = asApiResponseError(caught);
      setError(
        apiError?.message ||
          '기업 자료 처리 동의를 철회하지 못했습니다.',
      );
    } finally {
      setWithdrawing(false);
    }
  };

  if (!companyId) return null;

  const canCreate =
    Boolean(authorityBasis) &&
    confirmedAuthority &&
    confirmedProcessing &&
    confirmedExternal;

  return (
    <section
      className={`rounded-xl border ${
        selected
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-amber-200 bg-amber-50'
      } p-4 ${className}`}
      aria-label="기업 자료 처리 동의"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 rounded-lg p-2 ${
              selected
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              기업 자료 처리 동의
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              제안서 자동화 전에 회사 권한과 자료 처리 범위를 확인합니다.
            </p>
          </div>
        </div>
        {selected && (
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
            <CheckCircle className="mr-1 h-3.5 w-3.5" />
            동의 선택됨
          </span>
        )}
      </div>

      {loading ? (
        <div className="mt-4 flex items-center text-sm text-gray-600">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          활성 동의를 확인하는 중...
        </div>
      ) : (
        <>
          {compatible.length > 0 && (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
              <label className="flex-1 text-sm font-medium text-gray-700">
                사용할 활성 동의
                <select
                  value={value || ''}
                  onChange={(event) =>
                    selectAuthorization(event.target.value)
                  }
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  {compatible.map((authorization) => (
                    <option key={authorization.id} value={authorization.id}>
                      {formatDate(authorization.granted_at)} 승인 ·{' '}
                      {formatDate(authorization.expires_at)} 만료
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowConsent((current) => !current);
                    setShowWithdrawal(false);
                  }}
                  className="rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                >
                  {showConsent ? '새 동의 닫기' : '새 동의 등록'}
                </button>
                {selected && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowWithdrawal((current) => !current);
                      setShowConsent(false);
                      setError(null);
                      setNotice(null);
                    }}
                    className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    {showWithdrawal ? '철회 닫기' : '선택 동의 철회'}
                  </button>
                )}
              </div>
            </div>
          )}

          {!showConsent && selected && (
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
              <span>보유기간: {selected.retention_days}일</span>
              <span>
                외부 처리자: {selected.external_processors.join(', ')}
              </span>
              <span>고지: {selected.notice_version}</span>
            </div>
          )}

          {showWithdrawal && selected && (
            <div className="mt-4 space-y-3 rounded-lg border border-red-200 bg-white p-4">
              <p className="text-sm text-red-800">
                철회하면 이 동의로는 새로운 수집·분석·저장·조회·내보내기
                요청을 할 수 없고, 연결 자료는 삭제 절차 대상으로
                전환됩니다. 이미 외부 처리자에게 전달되어 진행 중인
                작업의 즉시 중단을 보장하지는 않습니다.
              </p>
              <label className="block text-sm font-medium text-gray-700">
                철회 사유
                <textarea
                  value={withdrawalReason}
                  onChange={(event) =>
                    setWithdrawalReason(event.target.value)
                  }
                  maxLength={1000}
                  rows={2}
                  placeholder="예: 담당 기업의 자료 이용 요청 철회"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleWithdraw}
                  disabled={
                    withdrawalReason.trim().length < 3 || withdrawing
                  }
                  className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {withdrawing && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  동의 철회
                </button>
              </div>
            </div>
          )}

          {showConsent && (
            <div className="mt-4 space-y-4 rounded-lg border border-amber-200 bg-white p-4">
              <div className="flex items-start gap-2 text-sm text-amber-800">
                <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <p>
                  직접 입력 및 기업 프로필에 포함된 기업 기본정보, 실적,
                  제품·서비스, 인증, 지식재산, 재무, 연락처 및 개인정보를
                  제안서 작성·평가·저장·조회·내보내기에 사용하며 최대
                  365일 보유합니다. 외부 AI 처리 시{' '}
                  {PROPOSAL_EXTERNAL_PROCESSORS.join(', ')}에 전송될 수
                  있습니다.
                </p>
              </div>

              <label className="block text-sm font-medium text-gray-700">
                기업 자료 제공 권한
                <select
                  value={authorityBasis}
                  onChange={(event) =>
                    setAuthorityBasis(
                      event.target.value as CompanyAuthorityBasis | '',
                    )
                  }
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="">권한 근거를 선택하세요</option>
                  <option value="company_representative">기업 대표자</option>
                  <option value="delegated_officer">
                    기업으로부터 위임받은 담당자
                  </option>
                </select>
              </label>

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={confirmedAuthority}
                  onChange={(event) =>
                    setConfirmedAuthority(event.target.checked)
                  }
                  className="mt-1 rounded border-gray-300 text-emerald-600"
                />
                <span>
                  위 기업 자료를 제공하고 처리에 동의할 적법한 권한이
                  있음을 확인합니다.
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={confirmedProcessing}
                  onChange={(event) =>
                    setConfirmedProcessing(event.target.checked)
                  }
                  className="mt-1 rounded border-gray-300 text-emerald-600"
                />
                <span>
                  자료 수집·분석·저장·조회·내보내기와 365일 보유, 철회
                  또는 만료 이후 이 동의로 새로운 처리 요청이
                  허용되지 않는다는 고지를 확인했습니다.
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={confirmedExternal}
                  onChange={(event) =>
                    setConfirmedExternal(event.target.checked)
                  }
                  className="mt-1 rounded border-gray-300 text-emerald-600"
                />
                <span>
                  AI 기능 사용 시 Anthropic 및 Perplexity가 승인된 외부
                  처리자로 자료를 처리하는 데 동의합니다.
                </span>
              </label>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3">
                <span className="text-xs text-gray-500">
                  고지 버전: {PROPOSAL_AUTHORIZATION_NOTICE_VERSION}
                </span>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={!canCreate || creating}
                  className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {creating && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  동의 등록 및 사용
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-100 p-3 text-sm text-emerald-800">
          <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{notice}</span>
        </div>
      )}
    </section>
  );
};

export default CompanyDataAuthorizationPanel;
